import { Select, Selection, SelectItem } from '@nextui-org/react'
import { memo } from 'react'
import { cm } from '~/utils/utils'

// When a Select has an embedded search input (listProps.topContent), NextUI's
// listbox grabs focus on open, so the search input never receives focus and the
// user can't type until they click it. Focus the search input when the dropdown
// opens, retrying across a few frames so we win against react-aria's one-time
// listbox autofocus and the popover open animation.
const focusEmbeddedSearchInput = () => {
  let attempts = 0
  const tryFocus = () => {
    const searchInput = document.querySelector(
      'input[placeholder*="Search"]'
    ) as HTMLInputElement | null
    if (searchInput && document.activeElement !== searchInput) {
      searchInput.focus({ preventScroll: true })
    }
    attempts += 1
    if (attempts < 15 && document.activeElement !== searchInput) {
      requestAnimationFrame(tryFocus)
    }
  }
  requestAnimationFrame(tryFocus)
}

interface SelectProps {
  className?: string
  placeholder?: string
  ariaLabel?: string
  size?: 'sm' | 'md' | 'lg'
  items: { key: string; label: string }[]
  selectedKeys?: string[]
  onSelectionChange?: (keys: Selection) => void
  disabled?: boolean
  label?: string
  startContents?: React.ReactNode[]
  triggerPadding?: string
  fontSize?: string
  listProps?: {
    topContent?: React.ReactNode
  }
}

export const CurieSelect = memo(function CurieSelect({
  className,
  items,
  ariaLabel,
  placeholder,
  size = 'sm',
  selectedKeys = ['Indexing...'],
  onSelectionChange = () => {},
  disabled = false,
  label = '',
  startContents,
  triggerPadding = '',
  fontSize = 'text-xs',
  listProps
}: SelectProps) {
  ariaLabel = ariaLabel || placeholder
  return (
    <Select
      labelPlacement='outside'
      radius='none'
      size={size}
      label={label}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={className}
      defaultSelectedKeys={selectedKeys}
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
      onOpenChange={isOpen => {
        if (isOpen && listProps?.topContent) {
          focusEmbeddedSearchInput()
        }
      }}
      isDisabled={disabled}
      classNames={{
        base: 'w-full border-box dark:bg-medium6 pb-4 px-[1px]',
        trigger: cm(
          'w-full bg-white dark:bg-medium6 data-[hover=true]:bg-light3 dark:data-[hover=true]:bg-medium2 select-none',
          'shadow-sm ring-1 ring-[#d4dce2] group-data-[focus=true]:bg-transparent rounded-xl',
          triggerPadding
        ),
        innerWrapper: 'truncate',
        popoverContent: cm('rounded-xl dark:bg-medium6', fontSize),
        value: cm(fontSize, 'select-none'),
        label: 'select-none',
        selectorIcon: 'select-none'
      }}
      listboxProps={listProps}
    >
      {items.map((item, index) => (
        <SelectItem
          key={item.key || index}
          className='caret-transparent no-close'
          startContent={startContents ? startContents[index] : null}
          classNames={{
            base: 'break-all',
            title: 'select-none',
            description: 'select-none'
          }}
          onMouseDown={e => {
            // Prevent the search input from losing focus when clicking on items
            const searchInput = document.querySelector(
              'input[placeholder*="Search"]'
            ) as HTMLInputElement
            if (searchInput && document.activeElement === searchInput) {
              e.preventDefault()
              // Use requestAnimationFrame and preventScroll to avoid scroll jumping
              requestAnimationFrame(() => {
                if (searchInput) {
                  searchInput.focus({ preventScroll: true })
                }
              })
            }
          }}
        >
          {item.label}
        </SelectItem>
      ))}
    </Select>
  )
})
